import React, { useEffect } from "react";
import {
  CheckBoxInput,
  FileInput,
  RadioInput,
  SelectInput,
  TextInput,
} from "../../components/common/ui/Inputs";
import { useDispatch, useSelector } from "react-redux";
import { facilityAction } from "../../_store";
import { masterActions } from "../../_store";
import formatPhoneNumber from "./../../_helpers/formatPhoneNumber";
import { facilitySchema } from "../../validation/facilitySchema";

function FacilityInformation({ errorMessage, setErrorMessage, fileInputRef }) {
  const dispatch = useDispatch();
  const facilityInformationData = useSelector((x) => x?.facility?.facilityData);
  const stateList = useSelector((x) => x?.master?.stateDrpList);
  const { Status } = useSelector((state) => state.dropdownOptions);

  useEffect(() => {
    dispatch(masterActions.GetStateList());
  }, []);

  // Replace first object label
  const updatedStateList = stateList.map((role, index) => {
    if (index === 0) {
      return { ...role, label: "-- Select --" }; // Replace the label of the first object
    }
    return role;
  });

  function removeBase64Prefix(base64String) {
    const prefixes = [
      "data:image/png;base64,",
      "data:image/jpeg;base64,",
      "data:image/jpg;base64,",
      "data:image/gif;base64,",
    ];

    for (const prefix of prefixes) {
      if (base64String.startsWith(prefix)) {
        return base64String.replace(prefix, "");
      }
    }

    return base64String;
  }

  const handleFileChange = async (e) => {
    const { name, files } = e?.target;
    if (name === "logoImage") {
      const file = files && files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const imageData = {
            name: files[0]?.name,
            type: files[0]?.type,
            size: files[0]?.size,
          };
          const FileTypes = [
            "image/jpeg",
            "image/png",
            "image/jpg",
            "image/gif",
          ];
          let UpdatedfacilityData = { ...facilityInformationData };
          UpdatedfacilityData["base64Data"] = FileTypes.includes(files[0]?.type)
            ? reader.result
            : "";
          UpdatedfacilityData["file"] = FileTypes.includes(files[0]?.type)
            ? removeBase64Prefix(reader.result)
            : "";
          UpdatedfacilityData["filename"] = FileTypes.includes(files[0]?.type)
            ? files[0]?.name
            : "";
          UpdatedfacilityData["logoImage"] = imageData;
          dispatch(facilityAction.setFacilityData(UpdatedfacilityData));
        };
        try {
          setErrorMessage((prev) => ({
            ...prev,
            [name]: "",
          }));
          const imageData = {
            name: files[0]?.name,
            type: files[0]?.type,
            size: files[0]?.size,
          };
          await facilitySchema.validateAsync(
            { [name]: imageData },
            { abortEarly: false }
          );
        } catch (err) {
          const fieldError = err.details.find((error) =>
            error.path.includes(name)
          );

          if (fieldError) {
            setErrorMessage((prev) => ({
              ...prev,
              [name]: fieldError.message, // Set the specific field error from Joi
            }));
          }
        }
        reader.readAsDataURL(file);
      } else {
        let UpdatedfacilityData = { ...facilityInformationData };
        UpdatedfacilityData["base64Data"] = "";
        UpdatedfacilityData["file"] = "";
        UpdatedfacilityData["filename"] = "";
        UpdatedfacilityData["logoImage"] = {
          name: "empty",
          type: "image/jpeg",
          size: 5 * 1024 * 1024,
        };
        dispatch(facilityAction.setFacilityData(UpdatedfacilityData));
        setErrorMessage((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  const handleChange = async (e, drp) => {
    let facilityData = { ...facilityInformationData };
    if (drp) {
      facilityData[drp] = e?.value ? e?.value : 0;
      if (drp === "StateNameID") {
        try {
          // Clear error if validation passes
          setErrorMessage((prev) => ({
            ...prev,
            [drp]: "", // Clear the specific field error
          }));
          // Validate the field using the Joi schema and handle both required and other validation
          await facilitySchema.validateAsync(
            { [drp]: e?.value ? e?.value : 0 },
            { abortEarly: false }
          );
        } catch (err) {
          // Find the error that matches the current field name
          const fieldError = err.details.find((error) =>
            error.path.includes(drp)
          );

          if (fieldError) {
            setErrorMessage((prev) => ({
              ...prev,
              [drp]: fieldError.message, // Set the specific field error from Joi
            }));
          }
        }
      }
    } else {
      const { name, value, checked, type, files } = e?.target;

      if (name === "FacilityName") {
        facilityData["FacilityName"] = value.replace(/[^a-zA-Z0-9 ]/g, "");
      } else if (name === "FacilityCode") {
        facilityData["FacilityCode"] = value.replace(/[^a-zA-Z0-9]/g, "");
      } else if (name === "PhoneNumber") {
        // const formattedNumber = formatPhoneNumber(value);
        // facilityData["PhoneNumber"] = formattedNumber;
        const input = e.target;
        const selectionStart = input.selectionStart;

        const digitsOnly = value.replace(/\D/g, "");
        const formattedNumber = formatPhoneNumber(digitsOnly);
        facilityData["PhoneNumber"] = formattedNumber;

        // Dispatch updated data
        dispatch(facilityAction.setFacilityData(facilityData));

        // Cursor fix
        requestAnimationFrame(() => {
          const inputElem = document.querySelector(`input[name="PhoneNumber"]`);
          if (inputElem) {
            const newCursorPos = calculateNewCursorPosition(
              selectionStart,
              value,
              formattedNumber
            );
            inputElem.setSelectionRange(newCursorPos, newCursorPos);
          }
        });
      } else if (name === "ZIP") {
        // For ZIP code, remove anything that is not a digit (limit to 5 digits)
        facilityData["ZIP"] = value.replace(/[^0-9]/g, "").slice(0, 5);
      } else if (name === "City") {
        // For ZIP code, remove anything that is not a digit (limit to 5 digits)
        facilityData["City"] = value.replace(/[^a-zA-Z]/g, "").slice(0, 40);
      } else {
        facilityData[name] =
          type === "checkbox"
            ? checked
              ? 1
              : 0
            : type === "radio"
              ? parseInt(value)
              : value;
      }
    }
    dispatch(facilityAction.setFacilityData(facilityData));
  };

  const calculateNewCursorPosition = (oldPos, oldValue, newValue) => {
    let rawIndex = 0;
    let newIndex = 0;

    while (rawIndex < oldPos && newIndex < newValue.length) {
      if (/\d/.test(oldValue[rawIndex])) {
        while (newIndex < newValue.length && !/\d/.test(newValue[newIndex])) {
          newIndex++;
        }
        newIndex++;
      }
      rawIndex++;
    }
    return newIndex;
  };

  const handleBlurFacilityInfo = async (e) => {
    let { name, value } = e.target;
    let facilityData = { ...facilityInformationData };
    try {
      // Clear error if validation passes
      setErrorMessage((prev) => ({
        ...prev,
        [name]: "", // Clear the specific field error
      }));
      facilityData[name] = value ? value.trim() : "";
      // Validate the field using the Joi schema and handle both required and other validation
      await facilitySchema.validateAsync(
        { [name]: value },
        { abortEarly: false }
      );
    } catch (err) {
      // Find the error that matches the current field name
      const fieldError = err.details.find((error) => error.path.includes(name));

      if (fieldError) {
        setErrorMessage((prev) => ({
          ...prev,
          [name]: fieldError.message, // Set the specific field error from Joi
        }));
      }
    }
    dispatch(facilityAction.setFacilityData(facilityData));
  };

  return (
    <div className="mt-3 mb-3">
      <h4
        className="inner-header-bg text-color"
        style={{ cursor: "pointer" }}
        title="To set the facility specific details"
      >
        {" "}
        <i className="fa-solid fa-circle-info me-2"></i>Facility Information{" "}
      </h4>
      <div className="row">
        <div className="col-sm-6 col-md-6 col-lg-4">
          <div className="form-floating mb-3">
            <TextInput
              id="FacilityCode"
              label="Code"
              name="FacilityCode"
              placeholder="Enter Code"
              required
              handleChange={(e) => handleChange(e)}
              maxLength={10}
              errorMessage={errorMessage}
              value={facilityInformationData?.FacilityCode}
              handleBlur={handleBlurFacilityInfo}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-4">
          <div className="form-floating mb-3">
            <TextInput
              name="FacilityName"
              id="FacilityName"
              label="Name"
              placeholder="Enter Name"
              handleChange={(e) => handleChange(e)}
              errorMessage={errorMessage}
              required
              value={facilityInformationData?.FacilityName}
              handleBlur={handleBlurFacilityInfo}
              maxLength={100}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-4">
          <div className="form-floating mb-3">
            <TextInput
              name="Email"
              id="Email"
              label="E-mail"
              placeholder="Enter E-mail"
              required
              handleChange={(e) => handleChange(e)}
              value={facilityInformationData?.Email}
              errorMessage={errorMessage}
              handleBlur={handleBlurFacilityInfo}
              maxLength={100}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-4">
          <div className="form-floating mb-3">
            <TextInput
              name="Address1"
              id="Address1"
              label="Address 1"
              placeholder="Enter Address 1"
              required
              handleChange={(e) => handleChange(e)}
              value={facilityInformationData?.Address1}
              errorMessage={errorMessage}
              handleBlur={handleBlurFacilityInfo}
              maxLength={50}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-4">
          <div className="form-floating mb-3">
            <TextInput
              name="Address2"
              id="Address2"
              label="Address 2"
              placeholder="Enter Address 2"
              handleChange={(e) => handleChange(e)}
              value={facilityInformationData?.Address2}
              errorMessage={errorMessage}
              maxLength={50}
              handleBlur={handleBlurFacilityInfo}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-4">
          <div className="form-floating mb-3">
            <TextInput
              name="City"
              id="City"
              label="City"
              placeholder="Enter City"
              handleChange={(e) => handleChange(e)}
              value={facilityInformationData?.City}
              errorMessage={errorMessage}
              maxLength={40}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-4">
          <div className="form-floating mb-3">
            <TextInput
              name="ZIP"
              id="ZIP"
              label="ZIP"
              placeholder="Enter ZIP"
              handleChange={(e) => handleChange(e)}
              errorMessage={errorMessage}
              value={
                facilityInformationData?.ZIP > 0
                  ? facilityInformationData?.ZIP
                  : ""
              }
              maxLength={5}
              handleBlur={handleBlurFacilityInfo}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-4">
          <div className="form-floating mb-3">
            <SelectInput
              options={updatedStateList}
              label="State"
              name="StateNameID"
              id="StateNameID"
              value={facilityInformationData?.StateNameID}
              isClearable={facilityInformationData?.StateNameID !== 0}
              required
              placeholder="Select State"
              handleChange={(e) => handleChange(e, "StateNameID")}
              errorMessage={errorMessage}
            //handleBlur={handleBlurFacilityInfo}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-4">
          <div className="form-floating mb-3">
            <TextInput
              name="PhoneNumber"
              id="PhoneNumber"
              label="Phone Number"
              placeholder="(000) 000-0000"
              required
              value={facilityInformationData?.PhoneNumber}
              errorMessage={errorMessage}
              handleChange={(e) => handleChange(e)}
              maxLength={15}
              handleBlur={handleBlurFacilityInfo}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-4">
          <div className="form-floating mb-3">
            <FileInput
              label="Logo"
              name="logoImage"
              id="Logo"
              ref={fileInputRef}
              handleFileChange={(e) => handleFileChange(e)}
              imageURL={
                facilityInformationData?.base64Data
                  ? facilityInformationData?.base64Data
                  : facilityInformationData?.LogoFile
                    ? "data:image/png;base64," + facilityInformationData?.LogoFile
                    : ""
              }
              errorMessage={errorMessage}
              acceptFormat={".jpg,.png,.jpeg,.gif"}
              isFacilityNotes={true}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-4 d-flex flex-wrap mt-4">
          <div className="form-floating mb-3">
            <CheckBoxInput
              label="Quarantine"
              name="IsQuarantineWordBlock"
              id="IsQuarantineWordBlock"
              handleClick={(e) => handleChange(e)}
              checked={facilityInformationData?.IsQuarantineWordBlock === 1}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-4">
          <div className="form-floating mb-3">
            <RadioInput
              name="ActiveStatus"
              id="FacilityActiveStatus"
              label="Status"
              options={Status}
              handleChange={(e) => handleChange(e)}
              selectedValue={facilityInformationData?.ActiveStatus}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(FacilityInformation);
